import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private readonly STORAGE_KEY = 'preferred-language';
  private readonly SUPPORTED_LANGUAGES = ['en', 'fr'];
  private readonly DEFAULT_LANGUAGE = 'en';
  
  private currentLanguageSubject = new BehaviorSubject<string>(this.DEFAULT_LANGUAGE);
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(private translateService: TranslateService) {
    this.initializeLanguage();
  }

  /**
   * Initialize language settings
   */
  private initializeLanguage(): void {
    
    // Set supported languages
    this.translateService.addLangs(this.SUPPORTED_LANGUAGES);
    this.translateService.setDefaultLang(this.DEFAULT_LANGUAGE);
    

    // Determine initial language
    const initialLanguage = this.determineInitialLanguage();
    
    this.setLanguage(initialLanguage);
  }

  /**
   * Determine the initial language to use
   */
  private determineInitialLanguage(): string {
    // 1. Check if user has a saved preference
    const savedLanguage = localStorage.getItem(this.STORAGE_KEY);
    if (savedLanguage && this.SUPPORTED_LANGUAGES.includes(savedLanguage)) {
      return savedLanguage;
    }

    // 2. Check browser language
    const browserLanguage = this.getBrowserLanguage();
    if (browserLanguage && this.SUPPORTED_LANGUAGES.includes(browserLanguage)) {
      return browserLanguage;
    }

    // 3. Fall back to default
    return this.DEFAULT_LANGUAGE;
  }

  /**
   * Get browser language
   */
  private getBrowserLanguage(): string | null {
    const browserLang = this.translateService.getBrowserLang();
    if (!browserLang) return null;

    // Check for exact match
    if (this.SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }

    // Check for language family (e.g., 'en-US' -> 'en')
    const languageFamily = browserLang.split('-')[0];
    if (this.SUPPORTED_LANGUAGES.includes(languageFamily)) {
      return languageFamily;
    }

    return null;
  }

  /**
   * Set the current language
   */
  setLanguage(languageCode: string): void {
    
    if (!this.SUPPORTED_LANGUAGES.includes(languageCode)) {
      languageCode = this.DEFAULT_LANGUAGE;
    }

    this.translateService.use(languageCode);
    
    this.currentLanguageSubject.next(languageCode);
    
    // Store preference
    localStorage.setItem(this.STORAGE_KEY, languageCode);
    
    // Update document language for accessibility
    document.documentElement.lang = languageCode;
    
    // Update document direction if needed (for RTL languages in future)
    this.updateDocumentDirection(languageCode);
    
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages(): string[] {
    return [...this.SUPPORTED_LANGUAGES];
  }

  /**
   * Check if a language is supported
   */
  isLanguageSupported(languageCode: string): boolean {
    return this.SUPPORTED_LANGUAGES.includes(languageCode);
  }

  /**
   * Get language display name
   */
  getLanguageDisplayName(languageCode: string): string {
    const languageNames: { [key: string]: string } = {
      'en': 'English',
      'fr': 'Français'
    };
    return languageNames[languageCode] || languageCode;
  }

  /**
   * Get language flag emoji
   */
  getLanguageFlag(languageCode: string): string {
    const flags: { [key: string]: string } = {
      'en': '🇺🇸',
      'fr': '🇫🇷'
    };
    return flags[languageCode] || '🌐';
  }

  /**
   * Update document direction based on language
   */
  private updateDocumentDirection(languageCode: string): void {
    // For now, all supported languages are LTR
    // In the future, if RTL languages are added, update this method
    document.documentElement.dir = 'ltr';
  }

  /**
   * Get translation with parameters
   */
  translate(key: string, params?: any): Observable<string> {
    return this.translateService.get(key, params);
  }

  /**
   * Get translation synchronously
   */
  translateSync(key: string, params?: any): string {
    return this.translateService.instant(key, params);
  }

  /**
   * Check if a translation key exists
   */
  hasTranslation(key: string): boolean {
    return this.translateService.instant(key) !== key;
  }

  /**
   * Get all translations for current language
   */
  getTranslations(): any {
    return this.translateService.translations[this.getCurrentLanguage()] || {};
  }

  /**
   * Reset to default language
   */
  resetToDefault(): void {
    this.setLanguage(this.DEFAULT_LANGUAGE);
  }

  /**
   * Clear saved language preference
   */
  clearSavedPreference(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
}
